import { Component, inject, computed, effect, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InsuranceStore, ContactStore, BankStore } from '../../core/stores/base.store';
import { Contact } from '../../core/models';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NumberFormatDirective } from '../../core/utils/comma.utils';
import { formatDateAsYYYYMMDD } from '../../core/utils/date.utils';
import { LoanUtils } from '../../core/utils/loan.utils';

@Component({
  selector: 'app-add-insurance',
  templateUrl: './add-insurance.component.html',
  styleUrls: ['./add-insurance.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatToolbarModule,
    MatCardModule,
    MatCheckboxModule,
    NumberFormatDirective
  ]
})
export class AddInsuranceComponent {
  private insuranceStore = inject(InsuranceStore);
  private fb = inject(FormBuilder);
  private contactStore = inject(ContactStore);
  private bankStore = inject(BankStore);
  private router = inject(Router);
  selectedInsurance = computed(() => this.insuranceStore.selected());
  isEdit = false;
  
  // Signal to track when a new insurance is created
  newInsuranceCreated = signal(false);
  
  // Signal to store transaction data for the effect
  transactionData = signal<any>(null);

  // Find the self contact (category = 'Self')
  selfContact = computed(() => {
    return this.contactStore.entities().find(contact => contact.category === 'Self');
  });
  
  // Computed property for insurance contacts
  insuranceContacts = computed(() => {
    return this.contactStore.entities().filter(contact => contact.category === 'Insurance');
  });

  // Computed property for banks
  banks = computed(() => {
    return this.bankStore.entities();
  });

  form: FormGroup = this.fb.group({
    id: [null],
    user_id: [JSON.parse(localStorage.getItem('user_profile')).id],
    contact_id: [null, Validators.required],
    face_value: [null, [Validators.required]],
    premium: [null, [Validators.required]],
    cash_value: [null, Validators.required],
    issue_date: [new Date(), Validators.required],
    paid_to_date: [this.getDefaultPaidToDate(), Validators.required],
    bank_id: [null],
    fundFromBank: [true]
  });

  constructor() {
    // Create an effect to watch for new insurance and create transactions when needed
    effect(() => {
      // Track both the flag and the insurance store entities
      const newInsuranceFlag = this.newInsuranceCreated();
      const transactionInfo = this.transactionData();
      const latestInsurances = this.insuranceStore.entities();
            
      // Only proceed if we have transaction data and the newInsuranceCreated flag is true
      if (transactionInfo && newInsuranceFlag) {        
        // Reset the flag immediately to prevent multiple executions
        this.newInsuranceCreated.set(false);
        
        // Get the latest created insurance
        if (latestInsurances.length > 0) {
          const latestInsurance = latestInsurances[latestInsurances.length - 1];
          
          // Create the bank transaction
          // Make sure all values are properly formatted
          const transaction = {
            ...transactionInfo,
            insurance_id: latestInsurance.id,
            amount: LoanUtils.parseFormattedNumber(transactionInfo.amount)
          };
          
          this.bankStore.createChild({
            child: transaction,
            endpoint: 'bank_transaction',
            collectionName: 'bank_transactions_collection'
          });
          
          // Navigate away after transaction is created
          this.router.navigate(['/insurance']);
        }
        
        // Clear the transaction data
        this.transactionData.set(null);
      }
    });
  }

  // Helper method to get default paid_to_date (one year from today)
  private getDefaultPaidToDate(): Date {
    const today = new Date();
    return new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
  }

  ngOnInit() {
    if (this.selectedInsurance()) {
      this.isEdit = true;
      this.form.patchValue(this.selectedInsurance());
    }

    // Set up conditional validation for bank_id based on fundFromBank
    this.form.get('fundFromBank')?.valueChanges.subscribe(checked => {
      const bankIdControl = this.form.get('bank_id');
      if (checked) {
        bankIdControl?.setValidators(Validators.required);
      } else {
        bankIdControl?.clearValidators();
      }
      bankIdControl?.updateValueAndValidity();
    });

    // Trigger initial validation
    this.form.get('fundFromBank')?.updateValueAndValidity();
  }

  onSubmit() {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();
      const fundFromBank = formValue.fundFromBank;
      
      // Format and convert values
      formValue.paid_to_date = formatDateAsYYYYMMDD(formValue.paid_to_date);
      formValue.face_value = LoanUtils.parseFormattedNumber(formValue.face_value);
      formValue.premium = LoanUtils.parseFormattedNumber(formValue.premium);
      formValue.cash_value = LoanUtils.parseFormattedNumber(formValue.cash_value);
      
      // Prepare transaction data if funding from bank
      if (fundFromBank && formValue.bank_id) {
        // Format the issue date using the utility function
        const issueDate = formatDateAsYYYYMMDD(formValue.issue_date);
        
        // Store transaction data for the effect
        this.transactionData.set({
          contact_id: this.selfContact()?.id,
          transaction_date: issueDate,
          amount: -formValue.premium, // Premium already parsed above
          bank_id: formValue.bank_id,
          category: 'Insurance Premium Payment',
        });
      }
      
      // Remove non-insurance fields before saving
      delete formValue.fundFromBank;
      if (!fundFromBank) {
        delete formValue.bank_id;
      }
      const issueDate = formatDateAsYYYYMMDD(formValue.issue_date);
      delete formValue.issue_date;
      
      // Save insurance
      if (this.isEdit) {
        this.insuranceStore.update(formValue);
        // Always navigate after update
        this.router.navigate(['/insurance']);
      } else {
        // Make a copy of the form value for the insurance entity
        const insuranceEntity = { ...formValue };
        delete insuranceEntity.id;
        delete insuranceEntity.bank_id;
        delete insuranceEntity.fundFromBank;
        
        this.insuranceStore.create({
          entity: insuranceEntity,
          endpoint: 'insurance'
        });
        
        // Set the flag to trigger the effect if funding from bank
        
        // Set the flag to trigger the effect if funding from bank
        if (fundFromBank && formValue.bank_id) {
          // Use setTimeout to ensure the store has time to update
          setTimeout(() => {
            this.newInsuranceCreated.set(true);
          }, 100);
          // Don't navigate - the effect will handle navigation after transaction is created
        } else {
          // If not funding from bank, navigate immediately
          this.router.navigate(['/insurance']);
        }
      }
    }
  }

  onCancel() {
    this.router.navigate(['/insurance']);
  }

  // Helper method to format contact display
  getContactDisplay(contact: Contact): string {
    if (!contact) return '';
    
    const organization = contact.organization ? `${contact.organization} - ` : '';
    const name = (contact.first_name || '') + ' ' + (contact.last_name || '');
    
    return organization + name.trim();
  }

  // Helper method for tracking contacts by ID
  trackContactById(index: number, contact: Contact): number {
    return contact.id;
  }
}
